<template>
  <Menu
    as="div"
    @click.prevent
    class="relative flex"
    ref="reference"
    :aria-label="ariaLabel"
    v-slot="{open}"
  >
    <MenuButton as="template" v-slot="{open}">
      <slot name="trigger" :open="open">
        <button
          class="shrink-0 btn md white group/button flex items-center justify-center gap-1"
          :class="buttonClass"
        >
          <span
            class="opacity-70 group-hover/button:opacity-100 group-hover/button:text-blue-500 grid grid-cols-1 grid-rows-1 justify-center items-center transition-all ease-out duration-150"
            role="presentation"
          >
            <s-icon
              name="close"
              size="18"
              class="row-start-1 col-start-1 transition-all ease-out duration-150"
              :class="!open ? 'opacity-0' : 'opacity-100'"
              aria-hidden="true"
            />
            <s-icon
              :name="icon || 'dots-vertical'"
              size="18"
              class="row-start-1 col-start-1 transition-all ease-out duration-150"
              :class="open ? 'opacity-0' : 'opacity-100'"
              aria-hidden="true"
            />
          </span>
          <span v-if="!label">{{ $t('menu') }}</span>
          <span v-else>{{ label }}</span>
        </button>
      </slot>
    </MenuButton>
    <Teleport to="body">
      <transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="open"
          ref="floating"
          :style="floatingStyles"
          :class="dropdownClass"
          class="flex flex-col"
        >
          <MenuItems
            class="flex-1 overflow-y-auto w-56 divide-y divide-gray-200 rounded-md shadow-lg ring-1 ring-black/5 focus:outline-none scroll-shadow"
          >
            <template v-for="(item, index) in items">
              <MenuItem :key="index" v-slot="{active}" v-if="item.show !== false">
                <component
                  :is="item.href ? SLink : 'button'"
                  :href="item.href"
                  :method="item.method"
                  :data="item.data"
                  :external="item.external"
                  @click="item.onClick"
                  :class="[
                    active
                      ? 'bg-gradient-to-r from-gray-50 to-gray-100 text-blue-700'
                      : 'text-blue-600',
                    'group/button flex gap-1.5 w-full items-center first:rounded-t-md last:rounded-b-md px-3 py-2 first:pt-3 last:pb-3',
                    item.disabled ? 'opacity-70 grayscale bg-gray-100 cursor-not-allowed' : '',
                  ]"
                  :disabled="item.disabled"
                >
                  <slot name="item" :item="item" :active="active">
                    <s-icon
                      v-if="item.icon"
                      :name="item.icon"
                      :class="active ? 'opacity-100 text-blue-500' : 'opacity-70'"
                      size="20"
                      aria-hidden="true"
                    />
                    {{ item.label }}
                  </slot>
                </component>
              </MenuItem>
            </template>
          </MenuItems>
        </div>
      </transition>
    </Teleport>
  </Menu>
</template>
<script setup lang="ts">
import {ref, watch} from 'vue';
import {Menu, MenuButton, MenuItem, MenuItems} from '@headlessui/vue';
import {useFloating, autoUpdate, offset, shift, size, flip} from '@floating-ui/vue';

import SLink from '../components/SLink.vue';
import SIcon from './SIcon.vue';

const props = defineProps<{
  label?: string;
  icon?: string;
  ariaLabel?: string;
  items: {
    label?: string;
    href?: string;
    method?: string;
    data?: any;
    as?: string;
    onClick?: (...args: any[]) => void;
    show?: boolean;
    icon?: string;
    external?: boolean;
    index?: number;
    disabled?: boolean;
  }[];
  dropdownClass?: string;
  buttonClass?: string;
}>();

const reference = ref<HTMLElement | null>(null);
const floating = ref<HTMLElement | null>(null);

const middleware = ref([
  offset(8),
  flip({
    padding: 16,
    fallbackStrategy: 'bestFit',
  }),
  size({
    padding: 16,
    apply({availableWidth, availableHeight, elements}) {
      Object.assign(elements.floating.style, {
        maxWidth: `${Math.max(0, availableWidth)}px`,
        maxHeight: `${Math.max(0, availableHeight)}px`,
      });
    },
  }),
]);

const {floatingStyles} = useFloating(reference, floating, {
  placement: 'bottom-end',
  middleware,
  whileElementsMounted: autoUpdate,
});
</script>
